import {Label, NumberInput, Separator, Switch} from 'platform/components';
import {HStack, Show, Text, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNotNil} from 'ramda';

import i18n from '../../../../../i18n/i18n';
import {NumericBoundBody, RelativeFilterConfigBody} from '../../../../../types/api/api';
import {SeparatorWithLabel} from '../../../../SeparatorWithLabel';
import {BoundaryRow} from './BoundaryRow';

type BoundariesSlice = Pick<
  RelativeFilterConfigBody,
  | 'year'
  | 'mileage'
  | 'mileagePct'
  | 'power'
  | 'powerPct'
  | 'price'
  | 'pricePct'
  | 'featureScore'
  | 'radius'
>;

interface BoundariesSectionProps {
  value: BoundariesSlice;
  onChange: (next: BoundariesSlice) => void;
  isReadOnly?: boolean;
}

type PairUnit = 'abs' | 'pct';

interface PairRowProps {
  label: string;
  abs: NumericBoundBody | null;
  pct: NumericBoundBody | null;
  absLabel: string;
  onChange: (abs: NumericBoundBody | null, pct: NumericBoundBody | null) => void;
  testId: string;
  isReadOnly?: boolean;
}

function PairBoundaryRow(props: PairRowProps) {
  const {abs, pct, absLabel, onChange, isReadOnly} = props;
  const [unit, setUnit] = useState<PairUnit>(isNotNil(pct) ? 'pct' : 'abs');

  const activeValue = unit === 'pct' ? pct : abs;

  const handleChange = (next: NumericBoundBody | null) => {
    if (unit === 'pct') {
      onChange(null, next);
    } else {
      onChange(next, null);
    }
  };

  const handleUnitChange = (u: PairUnit) => {
    setUnit(u);
    if (u === 'pct') {
      onChange(null, activeValue);
    } else {
      onChange(activeValue, null);
    }
  };

  return (
    <BoundaryRow
      label={props.label}
      value={activeValue}
      onChange={handleChange}
      unitLabels={{abs: absLabel, pct: '%'}}
      unit={unit}
      onUnitChange={handleUnitChange}
      isReadOnly={isReadOnly}
      data-testid={props.testId}
    />
  );
}

export function BoundariesSection(props: BoundariesSectionProps) {
  const {value, onChange, isReadOnly} = props;

  // Tracks the radius toggle independently of the numeric value so the input
  // can be cleared (null) without hiding the section or reverting to the default.
  const [radiusEnabled, setRadiusEnabled] = useState(isNotNil(value.radius));

  const handleRadiusToggle = (enabled: boolean) => {
    setRadiusEnabled(enabled);
    onChange({...value, radius: enabled ? value.radius : null});
  };

  return (
    <VStack spacing={3}>
      <SeparatorWithLabel label={i18n.t('entity.filterForm.savedFilters.dialog.boundaries')} />

      <BoundaryRow
        label={i18n.t('entity.filterForm.registration')}
        value={value.year}
        onChange={(year) => onChange({...value, year})}
        isReadOnly={isReadOnly}
        data-testid="savedFilters-boundaryYear"
      />

      <Separator spacing={1} />

      <PairBoundaryRow
        label={i18n.t('entity.filterForm.millage')}
        abs={value.mileage}
        pct={value.mileagePct}
        absLabel="km"
        onChange={(mileage, mileagePct) => onChange({...value, mileage, mileagePct})}
        isReadOnly={isReadOnly}
        testId="savedFilters-boundaryMileage"
      />

      <Separator spacing={1} />

      <PairBoundaryRow
        label={i18n.t('entity.filterForm.power')}
        abs={value.power}
        pct={value.powerPct}
        absLabel="kW"
        onChange={(power, powerPct) => onChange({...value, power, powerPct})}
        isReadOnly={isReadOnly}
        testId="savedFilters-boundaryPower"
      />

      <Separator spacing={1} />

      <PairBoundaryRow
        label={i18n.t('entity.filterForm.price')}
        abs={value.price}
        pct={value.pricePct}
        absLabel="EUR"
        onChange={(price, pricePct) => onChange({...value, price, pricePct})}
        isReadOnly={isReadOnly}
        testId="savedFilters-boundaryPrice"
      />

      <Separator spacing={1} />

      <BoundaryRow
        label={i18n.t('entity.filterForm.featureScore')}
        value={value.featureScore}
        onChange={(featureScore) => onChange({...value, featureScore})}
        unitLabels={{abs: '%', pct: '%'}}
        isReadOnly={isReadOnly}
        data-testid="savedFilters-boundaryFeatureScore"
      />

      <Separator spacing={1} />

      <HStack align="center" justify="space-between" spacing={2}>
        <HStack align="center" spacing={2}>
          <Switch value={radiusEnabled} onChange={handleRadiusToggle} isDisabled={isReadOnly} />
          <Label>{i18n.t('entity.filterForm.radius')}</Label>
        </HStack>
      </HStack>
      <Show when={radiusEnabled}>
        <HStack align="center" spacing={2}>
          <NumberInput
            value={value.radius ?? null}
            onChange={(km) => onChange({...value, radius: km ?? null})}
            isDisabled={isReadOnly}
          />
          <Text size="small" color="secondary">
            km
          </Text>
        </HStack>
      </Show>
    </VStack>
  );
}
